<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >
      <!-- Encabezado -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Usuarios
          </h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            Gestión de usuarios con acceso al sistema (Clientes Básico y Enterprise).
          </p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nuevo
        </button>
      </div>

      <!-- Barra de Filtros y Búsqueda -->
      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por nombre, username, email o documento..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          >
        </div>
        <select
          v-model="roleFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none"
        >
          <option value="">Todos los Roles</option>
          <option :value="1">Cliente Básico (1)</option>
          <option :value="3">Enterprise (3)</option>
        </select>
        <select
          v-model="statusFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none"
        >
          <option value="">Todos los Estados</option>
          <option :value="1">Activo</option>
          <option :value="0">Inactivo</option>
        </select>
      </div>

      <!-- Tabla Operativa -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Usuario</th>
                <th class="px-6 py-4 font-semibold">Documento</th>
                <th class="px-6 py-4 font-semibold">Rol & Empresa</th>
                <th class="px-6 py-4 font-semibold text-center">Plan</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="6" class="px-6 py-8 text-center text-slate-500">Cargando inquilinos...</td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="6" class="px-6 py-8 text-center text-slate-500">No se encontraron inquilinos que coincidan.</td>
              </tr>

              <tr
                v-else
                v-for="item in filteredList"
                :key="item.id"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <!-- Usuario -->
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <img
                      :src="item.photo_url || `https://ui-avatars.com/api/?name=${item.full_name}&background=10B981&color=fff`"
                      class="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                    />
                    <div>
                      <div class="font-bold text-slate-900 dark:text-slate-100">{{ item.full_name }}</div>
                      <div class="text-xs text-slate-500">{{ item.email }} • @{{ item.username }}</div>
                    </div>
                  </div>
                </td>

                <!-- Documento -->
                <td class="px-6 py-4 font-mono text-slate-600 dark:text-slate-400">
                  {{ item.document || 'N/A' }}
                </td>

                <!-- Rol -->
                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    {{ item.role_id === 3 ? '🏢 Enterprise' : '👤 Básico' }}
                  </span>
                  <div v-if="item.company" class="text-[10px] text-slate-400 mt-1">Cía ID: {{ item.company }}</div>
                </td>

                <!-- Premium -->
                <td class="px-6 py-4 text-center">
                  <Crown v-if="item.is_premium === 1" class="w-5 h-5 mx-auto text-amber-500" />
                  <span v-else class="text-xs text-slate-400">Free</span>
                </td>

                <!-- Estado -->
                <td class="px-6 py-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="item.is_active === 1 ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400'"
                  >
                    <span class="w-1.5 h-1.5 rounded-full" :class="item.is_active === 1 ? 'bg-emerald-500' : 'bg-slate-400'"></span>
                    {{ item.is_active === 1 ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>

                <!-- Acciones -->
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button
                      @click="openEdit(item)"
                      class="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Editar Inquilino"
                    >
                      <FileText class="w-5 h-5" />
                    </button>
                    <button
                      @click="deleteItem(item)"
                      class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar Inquilino"
                    >
                      <Trash2 class="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} registros en el sistema</span>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <TenantForm
      :show="showModal"
      :tenantToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, FileText, Trash2, Crown } from 'lucide-vue-next';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import TenantForm from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const roleFilter = ref('');
const showModal = ref(false);
const selectedItem = ref(null);

const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/users');
    let data = Array.isArray(res.data) ? res.data : (res.data.data || []);

    // FILTRO ESTRICTO: Solo Roles 1 y 3 (Inquilinos/Clientes)
    list.value = data.filter(user => user.role_id === 1 || user.role_id === 3);
  } catch (e) {
    showError('Error cargando los inquilinos');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value !== '') result = result.filter(i => i.is_active === statusFilter.value);
  if (roleFilter.value !== '') result = result.filter(i => i.role_id === roleFilter.value);

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => {
      return (i.full_name || '').toLowerCase().includes(term) ||
             (i.email || '').toLowerCase().includes(term) ||
             (i.username || '').toLowerCase().includes(term) ||
             (i.document || '').toLowerCase().includes(term);
    });
  }
  return result;
});

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar Inquilino?',
    text: "Esta acción no se puede deshacer.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/users/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Inquilino eliminado del sistema.');
      } catch (e) {
        showError('No se pudo eliminar el inquilino.');
      }
    }
  });
};
</script>